/**
 * The append-only activity thread for a task: a list of comments plus a form to
 * post new ones. Shared by the tasks page (expanded row) and the patient profile
 * timeline entry so both surfaces render the same thread.
 */
import { ArrowRightLeft, Circle, Moon, Sun } from 'lucide-react'
import { useState } from 'react'

import type { TaskComment, TaskResponse } from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import { Textarea } from '@/frontend/components/ui/textarea'
import { usePostTaskComment } from '@/frontend/features/tasks/use-post-task-comment'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { cn } from '@/frontend/lib/utils'

/** Renders the append-only comment thread for a task and a form to post new comments. */
export function TaskCommentThread({ task }: { task: TaskResponse }) {
  const [draft, setDraft] = useState('')
  const { mutate, isPending } = usePostTaskComment(task.id)
  const { data: currentStaff } = useGetAuthenticatedStaff()

  function handlePost() {
    const body = draft.trim()
    if (!body) return
    mutate({ body }, { onSuccess: () => setDraft('') })
  }

  return (
    <div className="mt-3 space-y-2">
      <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Thread
      </span>
      {task.comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No comments yet.</p>
      ) : (
        <div className="flex flex-col gap-1">
          {task.comments.map((comment, index) => {
            const key = `${comment.staffId}-${comment.createdAt}-${index}`
            if (isSystemComment(comment)) return <TaskEventItem key={key} comment={comment} />
            const previous = task.comments[index - 1]
            return (
              <TaskMessageItem
                key={key}
                comment={comment}
                isOwn={!!currentStaff && comment.staffId === currentStaff.id}
                showHeader={
                  !previous || isSystemComment(previous) || previous.staffId !== comment.staffId
                }
              />
            )
          })}
        </div>
      )}
      <div className="flex gap-2 items-start pt-1">
        <Textarea
          className="flex-1 min-h-[60px] text-sm resize-none"
          placeholder="Add a comment…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Submit on Ctrl+Enter / Cmd+Enter
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handlePost()
          }}
        />
        <Button
          className="h-7 text-sm shrink-0"
          disabled={isPending || !draft.trim()}
          onClick={handlePost}
        >
          Post
        </Button>
      </div>
    </div>
  )
}

// System comments carry the acting staff member's id, so the author name is the only marker.
function isSystemComment(comment: TaskComment) {
  return comment.authorName === 'System'
}

function formatShortTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function eventIcon(body: string) {
  if (body.startsWith('Task snoozed')) return Moon
  if (body.startsWith('Task unsnoozed')) return Sun
  if (body.startsWith('Task reassigned')) return ArrowRightLeft
  return Circle
}

// Trailing punctuation is excluded so "see https://x.com." doesn't link the full stop.
const URL_RE = /(https?:\/\/[^\s]*[^\s.,;:!?)\]'"])/

function renderCommentBody(body: string) {
  // split() with a capture group puts URLs at odd indexes.
  return body.split(URL_RE).map((part, i) =>
    i % 2 === 1 ? (
      <a
        // biome-ignore lint/suspicious/noArrayIndexKey: derived from an immutable string, never reordered
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2"
      >
        {part}
      </a>
    ) : (
      part
    )
  )
}

function TaskMessageItem({
  comment,
  isOwn,
  showHeader,
}: {
  comment: TaskComment
  isOwn: boolean
  showHeader: boolean
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 text-sm',
        isOwn ? 'items-end' : 'items-start',
        showHeader && 'mt-2'
      )}
    >
      {showHeader && (
        <div className="flex items-center gap-1.5 px-1 text-xs">
          <span className="font-medium">{comment.authorName}</span>
          <span
            className="text-muted-foreground"
            title={new Date(comment.createdAt).toLocaleString()}
          >
            {formatShortTime(comment.createdAt)}
          </span>
        </div>
      )}
      <p
        className={cn(
          'max-w-[80%] whitespace-pre-wrap break-words rounded-lg px-3 py-2',
          isOwn ? 'bg-primary/10' : 'bg-muted'
        )}
        title={showHeader ? undefined : new Date(comment.createdAt).toLocaleString()}
      >
        {renderCommentBody(comment.body)}
      </p>
    </div>
  )
}

function TaskEventItem({ comment }: { comment: TaskComment }) {
  const Icon = eventIcon(comment.body)
  return (
    <div className="flex items-start gap-2 px-1 py-0.5 text-xs text-muted-foreground">
      <Icon
        className={cn('mt-0.5 size-3 shrink-0', Icon === Circle && 'size-1.5 mt-1 fill-current')}
      />
      <span className="flex-1 whitespace-pre-wrap">{comment.body}</span>
      <span className="shrink-0" title={new Date(comment.createdAt).toLocaleString()}>
        {formatShortTime(comment.createdAt)}
      </span>
    </div>
  )
}
